<template>
  <v-container>
    <fragment v-if="userIsAuthenticated">
      <Title
        type="h2" class="font-weight-thin">
        {{ $t('language.baseLearningLanguage') }}
      </Title>
      <v-row class="mb-6">
        <v-col cols="12" sm="6">
          <v-select
            :label="$t('app.baseLanguage')"
            :items="languageList"
            v-model="languages.base"
            @change="setLanguage('base', $event)"
            :hint="$t('language.baseLanguageHint')"
            persistent-hint
            outlined />
        </v-col>
        <v-col cols="12" sm="6">
          <v-select
            :label="$t('app.learningLanguage')"
            :items="languageList"
            v-model="languages.learning"
            @change="setLanguage('learning', $event)"
            :hint="$t('language.learningLanguageHint')"
            persistent-hint
            outlined />
          <p v-if="duplicateLanguage" style="color: red;">
            {{ $t('language.duplicateLanguage') }}
          </p>
        </v-col>
      </v-row>
      <Title
        type="h2" class="font-weight-thin">
        {{ $t('language.languageSetManagement') }}
      </Title>
      <v-row class="mb-6">
        <v-col cols="12" sm="6">
          <v-select
            :label="$t('language.languageSets')"
            v-model="languageSet"
            :items="formattedLanguageSets"
            @change="getDeleteContentWarning"
            :hint="$t('language.languageSetsHint')"
            clearable
            persistent-hint
            outlined />
        </v-col>
        <v-col cols="12" sm="6">
          <v-btn
            @click="deleteLanguageSet"
            :disabled="!isValidToDelete"
            color="red" class="white--text" block>
            <v-icon left>
              delete
            </v-icon>{{ $t('app.delete') }}
          </v-btn>
          <div v-if="languageSet && isValidToDelete" class="red--text font-weight-light subtitle-2 pt-2">
            <div>
              {{ $t('language.LanguageSetsDeleteWarning') }}
            </div>
            <div>
              {{ deleteContentWarningLists > 1
                ? $t('language.LanguageSetsDeleteWarningLists', {amount: deleteContentWarningLists})
                : $t('language.LanguageSetsDeleteWarningList', {amount: deleteContentWarningLists}) }}
              |
              {{ deleteContentWarningListVocabularies > 1
                ? $t('language.LanguageSetsDeleteWarningVocabularies', {amount: deleteContentWarningListVocabularies})
                : $t('language.LanguageSetsDeleteWarningVocabulary', {amount: deleteContentWarningListVocabularies}) }}.
            </div>
          </div>
          <div v-if="languageSet && !isValidToDelete" class="red--text font-weight-light subtitle-2 pt-2">
            {{ $t('language.LanguageSetDefaultWarning') }}
          </div>
        </v-col>
      </v-row>
    </fragment>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import availableLanguages from '@/config/languages';
import updateDataFrom from '@/mixins/firestore/updateDataFrom';
import removeItemFrom from '@/mixins/firestore/removeItemFrom';
import getMyVocabularyLanguageSets from '@/mixins/vocabularies/getMyVocabularyLanguageSets';
import { USERS, VOCABULARIES } from '@/config/firestore';

export default {
  name: 'Settings',
  mixins: [updateDataFrom, removeItemFrom, getMyVocabularyLanguageSets],
  data: () => ({
    languages: {
      base: '',
      learning: '',
    },
    languageSets: [],
    languageSet: null,
    deleteContentWarning: [],
  }),
  computed: {
    ...mapGetters('user', ['userLanguages', 'userIsAuthenticated']),

    duplicateLanguage() {
      return this.languages.base === this.languages.learning;
    },
    languageList() {
      return this.$lang.getAllNativeNames();
    },
    formattedLanguageSets() {
      return this.languageSets
        .map((item) => item.split('_'))
        .map((item) => [this.$lang.getNativeName(item[0]), this.$lang.getNativeName(item[1])].join(' - '));
    },
    languageSetCode() {
      const [l1, l2] = this.languageSet.split('-');
      return `${this.$lang.getCode(l1.trim())}_${this.$lang.getCode(l2.trim())}`;
    },
    deleteContentWarningLists() {
      return Object.keys(this.deleteContentWarning).length;
    },
    deleteContentWarningListVocabularies() {
      const lists = Object.values(this.deleteContentWarning);
      const vocabularies = lists.map((list) => Object.keys(list.vocabularies).length);
      if (vocabularies.length) {
        return vocabularies.reduce((a, b) => a + b);
      }
      return 0;
    },
    isValidToDelete() {
      const userLanguage = `${this.languages.base} - ${this.languages.learning}`;
      return this.languageSet && userLanguage !== this.languageSet;
    },
  },
  methods: {
    ...mapActions('user', ['setUserLanguages']),

    setLanguage(type, name) {
      const languageCode = this.$lang.getCode(name);

      if (type === 'base') {
        this.updateDataFrom(USERS, { baseLanguage: languageCode });
      }

      if (type === 'learning') {
        this.updateDataFrom(USERS, { learningLanguage: languageCode });
      }

      this.setUserLanguages({
        base: this.$lang.getCode(this.languages.base),
        learning: this.$lang.getCode(this.languages.learning),
      });
    },
    initLanguageData() {
      this.languages = {
        base: this.$lang.getNativeName(this.userLanguages.base),
        learning: this.$lang.getNativeName(this.userLanguages.learning),
      };
    },
    async getLanguageSets() {
      this.languageSets = await this.getMyVocabularyLanguageSets();
    },
    async deleteLanguageSet() {
      await this.removeItemFrom(`${VOCABULARIES}_langs`, 'langs', this.languageSetCode);
      await this.getLanguageSets();
      this.languageSet = null;
    },
    async getDeleteContentWarning() {
      if (!this.languageSet) return;

      this.deleteContentWarning = await this.$firestore
        .collection(VOCABULARIES)
        .doc(this.userUid)
        .collection(this.languageSetCode)
        .doc(this.userUid)
        .get()
        .then((doc) => doc.data());
    },
  },
  async created() {
    this.initLanguageData();
    await this.getLanguageSets();
  },
};
</script>
